import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid, MenuItem, OutlinedInput, SelectChangeEvent } from "@mui/material";
import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import { AutoSuggest, Input, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import Modal from "Components/Generic/Modal";
import { useAppState } from "Context/StateContext";
import useYupValidation, { useHandleValidate } from "Hooks/useYupValidation";
import { Company } from "Models/company";
import { DespatchCrew } from "Models/despatch.crew";
import { Fleet } from "Models/fleet";
import { Member } from "Models/members/member";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";
import { getMuiPopupContainer } from "Themes/dispatchTheme";
import { getEquipmentName } from "Util/getEquipmentName";
import * as yup from "yup";

import useStyles, { BoxStyled, ChipStyled, SelectBranchOption } from "./styles";
import SkeletonComponent from "Components/Generic/Skeleton";
import { Branch } from "Models/branch";
import { Timezone } from "Models/timezone";
import { useQuery } from "@tanstack/react-query";
import { BranchKeys } from "Util/queries";
import BranchService from "Services/branch.service";
import TimezoneService from "Services/timezone.service";
import { Status } from "Util/enums";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useOperationDespatchContext } from "Modules/moving/contexts/DespatchContext";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { TaskDateUtils } from "Util/dateUtils";

interface Props {
  onSuccessCreate: (crew: DespatchCrew) => void;
}

export interface CreateCrewModalHandles {
  open: (currentDate: Date) => void;
}

const CreateCrewModalComponent: ForwardRefRenderFunction<CreateCrewModalHandles, Props> = (
  { onSuccessCreate },
  ref
) => {
  const { t: translate } = useTranslation();
  const { currentUser, userTimezone, formatWithUserTimezone } = useAppState();
  const classes = useStyles();

  const { useCreateCrew, getMembersList, getFleetList, getCompaniesWithConnection } =
    useDespatchActions();
  const { mutate: createCrew, isLoading: isCreating } = useCreateCrew((response, variables) => {
    onSuccessCreate({
      ...variables.data,
      ...response,
      isCrew: true,
      id: response.id!,
      companyId: response.companyId!,
    });
  });

  const [currentDate, setCurrentDate] = useState<Date | null>();
  const [company, setCompany] = useState<Company | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation<Team>(errors);

  const { isFullscreen } = useOperationDespatchContext();

  const handleOpenModal = (date: Date) => {
    setCurrentDate(date);
    setCompany(currentUser?.company ?? null);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setTeam(null);
    setCurrentDate(null);
    setCompany(null);
    setBranches([]);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpenModal,
  }));

  const { data: memberList } = getMembersList();
  const { data: fleetList } = getFleetList();
  const { data: companyList } = getCompaniesWithConnection();

  const leaderList = useMemo(
    () => memberList.filter((member) => member.userCompany?.leader),
    [memberList]
  );

  const timezoneInputValue = useMemo(() => {
    if (team?.timezone) {
      const formatted = team.timezone.nameFormatted || "";
      return formatted;
    }
    return "";
  }, [team?.timezone]);

  const memberListAvailable = useMemo(() => {
    if (!team?.memberLeaderId) return memberList || [];

    return memberList.filter((member) => member.id !== team.memberLeaderId);
  }, [team, memberList]);

  const { data: branchList, isFetching: fetchingBranchList } = useQuery({
    queryKey: BranchKeys.getAll([]),
    queryFn: async () =>
      await BranchService.getAll({
        id: currentUser?.branches?.map((branch) => branch.id),
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }).then(({ data }) => data),
    initialData() {
      return [];
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.companyId,
  });

  const { data: timezoneList, isFetching: fetchingTimezoneList } = useQuery({
    queryKey: ["timezone.getAll"],
    queryFn: async () =>
      await TimezoneService.getAll({
        sortBy: JSON.stringify([["nameFormatted", "ASC"]]),
      }).then(({ data }) => data),
    initialData() {
      return [];
    },
    refetchOnWindowFocus: false,
  });

  const schema = yup.object().shape({
    name: yup.string().required(translate("thisFieldIsRequired")),
    companyId: yup.number().required(translate("thisFieldIsRequired")),
    timezoneId: yup.number().required(translate("thisFieldIsRequired")),
    branches: yup
      .array()
      .min(1, translate("itShouldBeAtLeastOneBranch"))
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const handleValidate = () => {
    const validated = useHandleValidate(
      {
        ...team,
        companyId: company?.id,
      },
      schema,
      setErrors
    );

    return validated;
  };

  const onSubmit = () => {
    if (!team || !currentDate || !handleValidate()) return;

    const createdTo = TaskDateUtils.createTaskDateTime(
      {
        dateInput: formatWithUserTimezone(currentDate, "yyyy-MM-dd"),
      },
      userTimezone.name
    );

    const createdToZonedTime = utcToZonedTime(createdTo, userTimezone.name);

    const dataToCreate = {
      fleets: team.fleets || [],
      members: team.members || [],
      name: team.name,
      storageBoxes: [],
      tasks: [],
      companyId: company!.id!,
      createdTo: zonedTimeToUtc(createdToZonedTime, userTimezone.name),
      memberLeader: team?.memberLeader,
      memberLeaderId: team?.memberLeader?.id || team?.memberLeaderId,
      isAutoCreated: false,
      branches: team.branches || branches || [],
      timezoneId: team.timezoneId,
      timezoneName: team.timezoneName,
    };

    createCrew(
      {
        data: dataToCreate,
      },
      {
        onSuccess: () => {
          handleCloseModal();
        },
      }
    );
  };

  const renderChipValue = (selected: number[]) => {
    const selectedBranches: Branch[] = [];

    for (const branchId of selected) {
      const findBranch = branches.find((branch) => branch.id === branchId);

      if (findBranch) selectedBranches.push(findBranch);
    }

    return (
      <BoxStyled>
        {selectedBranches?.map((value: Branch, index: number) => {
          if (index <= 1) return <ChipStyled key={value.id} label={value.name} />;

          return null;
        })}
      </BoxStyled>
    );
  };

  const onSelectedBranch = (branchesId: number[]) => {
    if (branchList?.length && branchesId.includes(0)) {
      setBranches([...branchList]);
      setTeam(
        (state) =>
          ({
            ...state,
            branches: branchList,
          } as Team)
      );
    } else if (branchesId.includes(-1)) {
      setBranches([]);
      setTeam(
        (state) =>
          ({
            ...state,
            branches: [],
          } as Team)
      );
    } else {
      const branchArr: Branch[] = [];

      for (const branchId of branchesId) {
        const findBranch = branchList?.find((branch) => branch.id === Number(branchId));

        if (findBranch) branchArr.push(findBranch);
      }
      setBranches([...branchArr]);
      setTeam(
        (state) =>
          ({
            ...(state || {}),
            branches: branchArr,
          } as Team)
      );
    }
  };

  return (
    <Modal
      container={getMuiPopupContainer}
      className={classes.modalWrapper}
      open={open}
      handleClose={handleCloseModal}
      title={translate("createCrew")}
      description={translate("createATemporaryTeamoForTodaysTasks")}
      showActionButtons
      actionButtonProps={{
        textSave: translate("create"),
        customColorCancel: Color.TEXT_INFO,
        saveDisabled: isCreating,
        cancelDisabled: isCreating,
        loading: isCreating,
        onCancel: () => handleCloseModal(),
        onSave: onSubmit,
      }}
    >
      <Grid container flex-directio="column" spacing={3}>
        <Grid item xs={12}>
          <AutoSuggest
            disabled={isCreating}
            disablePortal={isFullscreen}
            label={translate("company")}
            getOptionLabel={(option: Company) => option.name ?? ""}
            getOptionSelected={(option: Company, value: Company) => option?.id === value?.id}
            value={company?.id}
            inputValue={company?.name}
            onChange={(company: Company) => {
              setCompany(company);
            }}
            required
            options={companyList}
          />
        </Grid>

        <Grid item xs={12}>
          <Input
            label={translate("name")}
            required
            value={team?.name}
            onChange={(event) =>
              setTeam(() => ({ ...(team || ({} as Team)), name: event.target.value }))
            }
            error={fieldHasError("name")}
            errorMessage={getErrorByField("name")}
          />
        </Grid>

        <Grid item xs={12}>
          <AutoSuggest
            disabled={isCreating}
            disablePortal={isFullscreen}
            label={translate("leaderUser")}
            inputValue={team?.memberLeader?.fullName || ""}
            getOptionLabel={(option: Member) => option?.fullName ?? ""}
            getOptionSelected={(option: Member, value: Member) => option?.id === value?.id}
            value={team?.memberLeaderId}
            onChange={(leader: Member) => {
              setTeam((prevState) => ({
                ...(prevState || ({} as Team)),
                memberLeader: leader !== null ? leader : undefined,
                memberLeaderId: leader !== null ? leader.id : undefined,
              }));
            }}
            options={leaderList}
          />
        </Grid>

        <Grid item xs={12}>
          <AutoSuggest
            multiple
            disabled={isCreating}
            disablePortal={isFullscreen}
            label={translate("member")}
            getOptionLabel={(option: Member) => option?.fullName ?? ""}
            getOptionSelected={(option: Member, value: Member) => option?.id === value?.id}
            value={team?.members || []}
            onChange={(members: Member[]) =>
              setTeam((prev) => ({ ...(prev ?? ({} as Team)), members }))
            }
            options={memberListAvailable}
          />
        </Grid>

        <Grid item xs={12}>
          <AutoSuggest
            multiple
            disabled={isCreating}
            disablePortal={isFullscreen}
            label={translate("equipments")}
            getOptionLabel={(option: Fleet) => {
              const { fullText } = getEquipmentName(
                option,
                translate("general"),
                translate("fleet")
              );

              return fullText;
            }}
            getOptionSelected={(option: Fleet, value: Fleet) => option?.id === value?.id}
            value={team?.fleets || []}
            onChange={(fleets: Fleet[]) => setTeam(() => ({ ...(team || ({} as Team)), fleets }))}
            options={fleetList?.length ? fleetList : []}
          />
        </Grid>

        <Grid item xs={12}>
          <SkeletonComponent isLoading={fetchingBranchList}>
            <Select
              dataCy="selectBranch"
              label={translate("branch")}
              className={classes.select}
              name="branchList"
              value={branches?.length ? branches?.map((branch) => branch?.id || -2) : []}
              error={fieldHasError("branches")}
              errorMessage={getErrorByField("branches")}
              onChange={(event: SelectChangeEvent<any>) => onSelectedBranch(event.target.value)}
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              multiple
              required
              renderValue={(selected) => renderChipValue(selected as number[])}
              placeholder={translate("select")}
            >
              {Number(branches?.length) < Number(branchList?.length) && (
                <MenuItem value={0}>
                  <SelectBranchOption>
                    <AddCircleOutline className="mr-3" />
                    {translate("addAll")}
                  </SelectBranchOption>
                </MenuItem>
              )}
              {branches?.length === branchList?.length && (
                <MenuItem value={-1}>
                  <SelectBranchOption isRemoving>
                    <RemoveCircleOutline className="mr-3" />
                    {translate("removeAll")}
                  </SelectBranchOption>
                </MenuItem>
              )}
              {branchList?.map((branch) => (
                <MenuItem value={branch.id} key={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>

        <Grid item xs={12}>
          <SkeletonComponent isLoading={fetchingTimezoneList}>
            <AutoSuggest
              disabled={fetchingTimezoneList}
              disablePortal={isFullscreen}
              label={translate("timezone")}
              getOptionLabel={(option: Timezone) => option?.nameFormatted || ""}
              getOptionSelected={(option: Timezone, value: Timezone) => option?.id === value?.id}
              value={team?.timezoneId}
              inputValue={timezoneInputValue}
              onChange={(timezone: Timezone) => {
                setTeam((prevState) => ({
                  ...(prevState || ({} as Team)),
                  timezone: timezone !== null ? timezone : undefined,
                  timezoneId: timezone !== null ? timezone.id : undefined,
                  timezoneName: timezone !== null ? timezone.name : undefined,
                }));
              }}
              options={timezoneList || []}
              error={fieldHasError("timezoneId")}
              errorMessage={getErrorByField("timezoneId")}
              required
            />
            {/* <Select
              dataCy="selectTimezone"
              label={translate("timezone")}
              name="timezoneList"
              value={team?.timezoneId}
              error={fieldHasError("timezoneId")}
              errorMessage={getErrorByField("timezoneId")}
              onChange={(event) =>
                setTeam({ ...(team || {}), timezoneId: Number(event.target.value) } as Team)
              }
              required
              placeholder={translate("select")}
            >
              {timezoneList?.map((timezoneList) => (
                <MenuItem value={timezoneList.id} key={timezoneList.id}>
                  {timezoneList.name}
                </MenuItem>
              ))}
            </Select> */}
          </SkeletonComponent>
        </Grid>
      </Grid>
    </Modal>
  );
};

const CreateCrewModal = forwardRef(CreateCrewModalComponent);

export default CreateCrewModal;
